import { DBSystemCode } from '@home/_config/config';
import AI_SEARCH_PAGE_SUPPORT_CONFIG_MAP from '../_config/pageSupport';

import type { TDBSystemCode, IAISearchPageRouteItem, TAISearchPageSupportConfigMap } from '@home/types/aiSearch';

interface IGetAISearchPageSupportResultParams {
  system?: string;
  url?: string;
  pathname?: string;
  configMap?: TAISearchPageSupportConfigMap;
}

interface IAISearchPageSupportResult {
  system?: TDBSystemCode;
  pathname: string;
  supportsFilters: boolean;
}

const SYSTEM_CODE_MAP: Record<string, TDBSystemCode> = {
  [DBSystemCode.Home]: DBSystemCode.Home,
  [DBSystemCode.Intelligence]: DBSystemCode.Intelligence,
  [DBSystemCode.Dashboard]: DBSystemCode.Dashboard,
  [DBSystemCode.Opinions]: DBSystemCode.Opinions,
  [DBSystemCode.Marketing]: DBSystemCode.Marketing,
  [DBSystemCode.Globalpedia]: DBSystemCode.Globalpedia,
  opinion: DBSystemCode.Opinions,
  gpop: DBSystemCode.Globalpedia,
  'g-pop': DBSystemCode.Globalpedia,
  gp: DBSystemCode.Globalpedia,
};

const SYSTEM_PREFIX_LIST: Array<{ prefix: string; system: TDBSystemCode }> = [
  { prefix: '/home', system: DBSystemCode.Home },
  { prefix: '/intelligence', system: DBSystemCode.Intelligence },
  { prefix: '/dashboard', system: DBSystemCode.Dashboard },
  { prefix: '/opinion', system: DBSystemCode.Opinions },
  { prefix: '/marketing', system: DBSystemCode.Marketing },
  { prefix: '/g-pop', system: DBSystemCode.Globalpedia },
];

const DEFAULT_ORIGIN = 'https://databrain.intlgame.com';

const getBaseOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  return DEFAULT_ORIGIN;
};

const normalizePathname = (pathname = ''): string => {
  if (!pathname) {
    return '';
  }

  const normalizedPathname = pathname.trim();

  if (!normalizedPathname) {
    return '';
  }

  const pathnameWithoutTrailingSlash = normalizedPathname.replace(/\/+$/, '');
  return pathnameWithoutTrailingSlash || '/';
};

const escapeRegExp = (value: string): string => {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const getTemplateRegExp = (templatePathname: string): RegExp => {
  const normalizedTemplatePathname = normalizePathname(templatePathname);

  if (normalizedTemplatePathname === '/') {
    return /^\/$/;
  }

  const templateSegments = normalizedTemplatePathname.split('/').filter(Boolean);
  const templatePattern = templateSegments
    .map((segment) => {
      if (/^:[A-Za-z0-9_]+\?$/.test(segment)) {
        return '(?:/[^/]+)?';
      }

      if (/^:[A-Za-z0-9_]+$/.test(segment)) {
        return '/[^/]+';
      }

      return `/${escapeRegExp(segment)}`;
    })
    .join('');

  return new RegExp(`^${templatePattern}$`);
};

export const normalizeAISearchSystemCode = (system = ''): TDBSystemCode | undefined => {
  const normalizedSystem = system.trim().toLowerCase();

  if (!normalizedSystem) {
    return undefined;
  }

  return SYSTEM_CODE_MAP[normalizedSystem] ?? normalizedSystem;
};

export const getAISearchTargetPathname = (url = ''): string => {
  const normalizedUrl = url.trim();

  if (!normalizedUrl) {
    return '';
  }

  try {
    return normalizePathname(new URL(normalizedUrl, getBaseOrigin()).pathname);
  } catch {
    return normalizePathname(normalizedUrl.split('?')[0]?.split('#')[0] || '');
  }
};

export const inferAISearchSystemCodeByPathname = (pathname = ''): TDBSystemCode | undefined => {
  const normalizedPathname = normalizePathname(pathname);

  return SYSTEM_PREFIX_LIST.find((item) => normalizedPathname.startsWith(item.prefix))?.system;
};

export const isAISearchRouteMatched = (pathname: string, route: IAISearchPageRouteItem): boolean => {
  const normalizedPathname = normalizePathname(pathname);
  const normalizedRoutePathname = normalizePathname(route?.pathname || '');

  if (!normalizedPathname || !normalizedRoutePathname) {
    return false;
  }

  if (route.matchType === 'template') {
    return getTemplateRegExp(normalizedRoutePathname).test(normalizedPathname);
  }

  return normalizedPathname === normalizedRoutePathname;
};

export const isAISearchPageSupportsFilters = (params: IGetAISearchPageSupportResultParams): boolean => {
  const configMap = params.configMap || AI_SEARCH_PAGE_SUPPORT_CONFIG_MAP;
  const targetPathname = normalizePathname(params.pathname || getAISearchTargetPathname(params.url || ''));
  const targetSystem =
    normalizeAISearchSystemCode(params.system || '') || inferAISearchSystemCodeByPathname(targetPathname);

  if (!targetSystem || !targetPathname) {
    return true;
  }

  const systemConfig = configMap[targetSystem];

  if (!systemConfig) {
    return true;
  }

  const isMatched = systemConfig.routes.some((route: IAISearchPageRouteItem) => {
    return isAISearchRouteMatched(targetPathname, route);
  });

  if (systemConfig.mode === 'blacklist') {
    return !isMatched;
  }

  return isMatched;
};

export const getAISearchPageSupportResult = (
  params: IGetAISearchPageSupportResultParams,
): IAISearchPageSupportResult => {
  const pathname = normalizePathname(params.pathname || getAISearchTargetPathname(params.url || ''));
  const system = normalizeAISearchSystemCode(params.system || '') || inferAISearchSystemCodeByPathname(pathname);

  return {
    system,
    pathname,
    supportsFilters: isAISearchPageSupportsFilters({
      ...params,
      pathname,
      system,
    }),
  };
};
